import { createSelector } from '@reduxjs/toolkit';

import { App } from '@playq/octopus2-apps';
import { AppID, Tag } from '@playq/octopus-common';
import type { PermModule } from '@playq/octopus2-auth';
import { Perms, UserRole, AccessLevel } from '@playq/octopus2-auth';

import type { RootState } from '/store/RootState';

import type { IModulePerms, IPermissionsAccess } from './state';

const selectAuthRole = (state: RootState): UserRole => state.auth.role;
const selectAuthCompanyPermsRaw = (state: RootState) => state.auth.companyPerms;

const selectAuthCompanyPerms = createSelector([selectAuthCompanyPermsRaw], (raw) => new Perms(raw));

export const baseSelectors = {
  state: (state: RootState) => state.app,
  appRaw: (state: RootState) => state.app.app,
  appIDRaw: (state: RootState) => state.app.app?.id,
  appFingerprintIDRaw: (state: RootState) => state.app.app?.fingerprintID,
  error: (state: RootState) => state.app.error,
  permsRaw: (state: RootState) => state.app.perms,
};

const selectPerms = createSelector(baseSelectors.permsRaw, (rawPerms) => (rawPerms ? new Perms(rawPerms) : undefined));

const selectAccessScope = createSelector(
  selectAuthCompanyPerms,
  baseSelectors.appRaw,
  selectPerms,
  (companyPerm: Perms, app, appPerms) =>
    (app && appPerms ? { ...companyPerm.access, ...appPerms.access } : companyPerm.access) as IPermissionsAccess
);

export const appSelectors = {
  app: createSelector(baseSelectors.appRaw, (rawApp) => (rawApp ? new App(rawApp) : undefined)),
  appRouteName: createSelector(baseSelectors.appRaw, (rawApp) => (rawApp ? rawApp.routeName : undefined)),
  appAsTag: createSelector(baseSelectors.appIDRaw, (rawID) =>
    rawID ? new Tag({ key: 'app', value: new AppID(rawID).id.toString() }) : undefined
  ),
  appID: createSelector(baseSelectors.appIDRaw, (rawID) => (rawID ? new AppID(rawID) : undefined)),
  perms: selectPerms,
  accessScope: selectAccessScope,
  moduleAccess: (mod: PermModule) =>
    createSelector(selectAuthRole, selectAccessScope, (role, scope): IModulePerms => {
      const access = role === UserRole.Admin || role === UserRole.SuperAdmin ? AccessLevel.WriteLive : scope[mod];

      return {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        canRead: !!access && access !== AccessLevel.None,
        canWrite: access === AccessLevel.Write || access === AccessLevel.WriteLive,
        canWriteLive: access === AccessLevel.WriteLive,
      };
    }),
  moduleAccessEx: (mod: PermModule) =>
    createSelector(selectAuthRole, selectPerms, (role: UserRole, p?: Perms) => {
      if (role === UserRole.Admin || role === UserRole.SuperAdmin) {
        return AccessLevel.WriteLive;
      }

      const map = p?.access ?? {};
      const access = map[mod];
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (!access) {
        return AccessLevel.None;
      }

      return access;
    }),
};
